import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENT_CONVERSATIONS_URL } from '../../constants';
import type { AgentConversation, AgentMessage } from '../../domain/types';
import { CitationLink } from './AssistantRegister';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

const DOCK_STORAGE_KEY = 'procure-flo-assistant-dock';

export function AssistantDock({
  authFetch,
  onPinnedChange
}: {
  authFetch: AuthFetch;
  onPinnedChange?: (pinnedOpen: boolean) => void;
}): ReactElement {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(() => readPinned());
  const [conversation, setConversation] = useState<AgentConversation | null>(null);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [panelTop, setPanelTop] = useState(72);

  useEffect(() => {
    try {
      localStorage.setItem(DOCK_STORAGE_KEY, pinned ? 'dock' : 'float');
    } catch {
      /* preference is optional */
    }
  }, [pinned]);

  useEffect(() => {
    onPinnedChange?.(pinned && open);
  }, [onPinnedChange, open, pinned]);

  useEffect(() => {
    const header = document.querySelector('.app-header');
    if (!header) {
      return;
    }
    const apply = () => setPanelTop(Math.ceil(header.getBoundingClientRect().height) + 8);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        className="assistant-dock-tab"
        data-testid="assistant-dock-tab"
        aria-expanded={false}
        onClick={() => setOpen(true)}
      >
        Assistant
      </button>
    );
  }

  const lastAssistant = conversation
    ? [...conversation.messages].reverse().find((message) => message.role === 'assistant')
    : undefined;

  async function onAsk(e: FormEvent): Promise<void> {
    e.preventDefault();
    const text = question.trim();
    if (!text || sending) {
      return;
    }
    setSending(true);
    setError('');
    try {
      let current = conversation;
      if (!current) {
        const created = await authFetch(AGENT_CONVERSATIONS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: text.slice(0, 80) })
        });
        if (!created.ok) {
          setError(
            created.status === 403
              ? 'You do not have permission to open Assistant.'
              : 'Could not start a conversation.'
          );
          return;
        }
        current = (await created.json()) as AgentConversation;
        setConversation(current);
      }
      const res = await authFetch(`${AGENT_CONVERSATIONS_URL}/${encodeURIComponent(current.id)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text })
      });
      if (res.status === 503) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Foundry chat is not configured.');
        setQuestion('');
        return;
      }
      if (!res.ok) {
        setError('Could not send the question.');
        return;
      }
      setConversation((await res.json()) as AgentConversation);
      setQuestion('');
    } finally {
      setSending(false);
    }
  }

  return (
    <aside
      className={`side-panel assistant-dock${pinned ? ' assistant-dock--pinned' : ''}`}
      aria-label="Assistant chat"
      data-testid="assistant-dock"
      style={
        pinned
          ? { top: panelTop, height: `calc(100vh - ${panelTop + 16}px)`, maxHeight: `calc(100vh - ${panelTop + 16}px)` }
          : { top: panelTop }
      }
    >
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          <h3>Assistant</h3>
        </div>
        <div className="side-panel-actions">
          <button
            type="button"
            className="icon-close"
            aria-pressed={pinned}
            aria-label={pinned ? 'Float' : 'Dock'}
            title={pinned ? 'Float' : 'Dock'}
            data-testid="assistant-dock-pin"
            onClick={() => setPinned((current) => !current)}
          >
            {pinned ? <FloatIcon /> : <DockIcon />}
          </button>
          <button type="button" className="secondary" onClick={() => navigate('/assistant')}>
            History
          </button>
          <button type="button" className="icon-close" onClick={() => setOpen(false)} aria-label="Close assistant">
            ×
          </button>
        </div>
      </div>
      <div className="side-panel-body">
        {error && <p className="error">{error}</p>}
        <section className="nested-card" aria-label="Thread">
          <h4 className="form-section-title">Thread</h4>
          {!conversation || conversation.messages.length === 0 ? (
            <p className="hint">Ask a question about prices, wells, or purchase orders in your region.</p>
          ) : (
            conversation.messages.map((message) => <DockMessage key={message.id} message={message} />)
          )}
        </section>
        {lastAssistant && lastAssistant.citations.length > 0 && (
          <section className="nested-card" aria-label="Citations">
            <h4 className="form-section-title">Citations</h4>
            <ul>
              {lastAssistant.citations.map((cite) => (
                <li key={`${cite.sourceType}-${cite.sourceId}`}>
                  <CitationLink cite={cite} />
                  {cite.vendorName ? ` · ${cite.vendorName}` : ''}
                  {cite.unitPrice != null ? ` · ${cite.unitPrice}` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}
        <form className="grid" data-testid="assistant-dock-composer" onSubmit={(e) => void onAsk(e)}>
          <label>
            Question
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} />
          </label>
          <button type="submit" disabled={sending || !question.trim()}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      </div>
    </aside>
  );
}

function DockIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fillRule="evenodd"
        d="M4 5.25h16A1.75 1.75 0 0 1 21.75 7v10A1.75 1.75 0 0 1 20 18.75H4A1.75 1.75 0 0 1 2.25 17V7A1.75 1.75 0 0 1 4 5.25Zm2 2.5h7.1v8.5H6v-8.5Z"
      />
    </svg>
  );
}

function FloatIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fillRule="evenodd"
        d="M3.25 4.5h13.5A1.5 1.5 0 0 1 18.25 6v7.25A1.5 1.5 0 0 1 16.75 14.75H3.25A1.5 1.5 0 0 1 1.75 13.25V6A1.5 1.5 0 0 1 3.25 4.5Zm1.85 2.15h9.8v5.95h-9.8V6.65Z"
      />
      <path d="M11 11.25h8.15A1.35 1.35 0 0 1 20.5 12.6v5.9a1.35 1.35 0 0 1-1.35 1.35H11a1.35 1.35 0 0 1-1.35-1.35v-5.9A1.35 1.35 0 0 1 11 11.25Z" />
    </svg>
  );
}

function readPinned(): boolean {
  try {
    return localStorage.getItem(DOCK_STORAGE_KEY) === 'dock';
  } catch {
    return false;
  }
}

function DockMessage({ message }: { message: AgentMessage }): ReactElement {
  return (
    <p>
      <strong>{message.role === 'user' ? 'You' : 'Assistant'}: </strong>
      {message.content}
    </p>
  );
}
